import "./campaigns.css";

import { Button, Modal, ModalBody, ModalHeader } from "reactstrap";
import React, { useEffect, useMemo, useState } from "react";
import { companyAccent, portfolioMetrics } from "./components/format";
import { hasAdminAccess, hasBackofficeAccess } from "../services/roles";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import BulkExportDrawer from "./bulkExportDrawer";
import { CampaignTable } from "./components/CampaignTable";
import { CampaignTile } from "./components/CampaignTile";
import { EmptyState } from "../components/EmptyState";
import { FilterBar } from "./components/FilterBar";
import { LevelHeader } from "./components/LevelHeader";
import LoadingImg from "../components/loading";
import { PlotRatingConfig } from "./editPlotRatingConfig/plotRatingConfig";
import { CfSelect as Select } from "../components/CfSelect";
import { ViewToggle } from "./components/ViewToggle";
import { fetchCrops } from "../actions/crops";
import { fetchUserContractsTrials } from "../actions/user";
import { hasFeaturesRole } from "../users/rolesUtil";
import queryString from "query-string";
import { removeAllContractsCurrentUser } from "../actions/company";
import { requestUploadSummaryByCampaign } from "../services/backendRequests";
import { requiresClientAction } from "../constants";

const SORTS = {
  company: (a, b) =>
    a.company.name.localeCompare(b.company.name) ||
    a.name.localeCompare(b.name),
  name: (a, b) => a.name.localeCompare(b.name),
  sites: (a, b) => (b.total_sites ?? 0) - (a.total_sites ?? 0),
};

const SORT_OPTIONS = [
  { value: "company", label: "Sort: Company" },
  { value: "name", label: "Sort: Name" },
  { value: "sites", label: "Sort: Most sites" },
];

// Level 1: bento grid of campaigns (contract infos).
const CampaignsPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const contracts = useSelector((store) => store.user.contracts) ?? [];
  const loading = useSelector((store) => store.user.contractsLoading);
  const user = useSelector((store) => store.user);

  const [editedContract, setEditedContract] = useState(null);
  const [query, setQuery] = useState("");
  const [company, setCompany] = useState([]);
  const [sort, setSort] = useState("company");
  const [view, setView] = useState("grid");
  const [groupBy, setGroupBy] = useState(false);
  const [uploads, setUploads] = useState({});
  const [actionRequired, setActionRequired] = useState(false);
  const [isBulkExportOpen, setIsBulkExportOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchUserContractsTrials());
    dispatch(fetchCrops());
    requestUploadSummaryByCampaign(user)
      .then(setUploads)
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Preselect a company when arriving from the global search (?company=<id>).
  useEffect(() => {
    const { company: companyParam } = queryString.parse(location.search);
    if (companyParam) setCompany([companyParam]);
  }, [location.search]);

  // Single campaign: skip the list, go straight in. replace() so Back doesn't
  // bounce the user right back here into a redirect loop.
  useEffect(() => {
    if (!loading && contracts.length === 1)
      history.replace(`/campaigns/${contracts[0].id}`);
  }, [loading, contracts, history]);

  const actionsVisible =
    hasBackofficeAccess(user.self) || hasAdminAccess(user.self);
  // The drawer only lists the campaigns where the user may read features.
  const canExport = contracts.some((contract) =>
    hasFeaturesRole(contract.roles)
  );
  const canRemoveAccess = actionsVisible && contracts.length > 0;

  const companyOptions = useMemo(() => {
    const map = new Map();
    contracts.forEach((contract) =>
      map.set(contract.company.id, contract.company.name)
    );
    return [...map.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [contracts]);

  // Total sites needing client action across every campaign, drives the toggle
  // label; the toggle itself keeps only campaigns that contain such a site.
  const actionCount = contracts.reduce(
    (total, contract) =>
      total + (contract.sites ?? []).filter(requiresClientAction).length,
    0
  );

  // Filter + sort. Every campaign renders as a uniform tile (no bento sizing).
  const tiles = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = contracts.filter((contract) => {
      const matchesQuery =
        !needle ||
        contract.name.toLowerCase().includes(needle) ||
        contract.company.name.toLowerCase().includes(needle);
      const matchesCompany =
        company.length === 0 || company.includes(contract.company.id);
      const matchesAction =
        !actionRequired || (contract.sites ?? []).some(requiresClientAction);
      return matchesQuery && matchesCompany && matchesAction;
    });
    return [...filtered].sort(SORTS[sort]);
  }, [contracts, query, company, sort, actionRequired]);

  // Grouped view: bucket the (already filtered + sorted) tiles by company, one
  // section per company. Companies are ordered alphabetically; campaigns keep
  // the active sort order within each section.
  const groups = useMemo(() => {
    if (!groupBy) return [];
    const byCompany = new Map();
    tiles.forEach((campaign) => {
      const { id } = campaign.company;
      if (!byCompany.has(id))
        byCompany.set(id, { company: campaign.company, campaigns: [] });
      byCompany.get(id).campaigns.push(campaign);
    });
    return [...byCompany.values()].sort((a, b) =>
      a.company.name.localeCompare(b.company.name)
    );
  }, [tiles, groupBy]);

  // Shared, uniform tile renderer for both the flat grid and the grouped view.
  const renderTile = (campaign) => (
    <CampaignTile
      key={campaign.id}
      campaign={campaign}
      actionsVisible={actionsVisible}
      uploads={uploads[campaign.id]}
      onOpen={(c) => history.push(`/campaigns/${c.id}`)}
      onEditPlotRating={(c) => setEditedContract(c)}
      onOpenAdmin={(c) =>
        history.push(`/administration/company/${c.company.id}/contract/${c.id}`)
      }
    />
  );

  // Renders one set of campaigns in the active density: flat bento grid or table.
  const renderCampaigns = (list) =>
    view === "list" ? (
      <CampaignTable
        campaigns={list}
        actionsVisible={actionsVisible}
        onOpen={(c) => history.push(`/campaigns/${c.id}`)}
        onEditPlotRating={(c) => setEditedContract(c)}
        onOpenAdmin={(c) =>
          history.push(
            `/administration/company/${c.company.id}/contract/${c.id}`
          )
        }
      />
    ) : (
      <div className="cf-bento">{list.map((c) => renderTile(c))}</div>
    );

  // Self-service: a user can drop their own access to every campaign at once.
  const removeAllAccess = () => {
    const contractIds = contracts.map((contract) => contract.id);
    if (contractIds.length === 0) return;
    if (
      !window.confirm(
        "Remove your access to all campaigns? This cannot be undone."
      )
    )
      return;
    dispatch(removeAllContractsCurrentUser(contractIds));
  };

  return (
    <div className="page-content container-fluid cf-page">
      <Modal
        size="md"
        isOpen={Boolean(editedContract)}
        toggle={() => setEditedContract(null)}
      >
        <ModalHeader toggle={() => setEditedContract(null)}>
          <h4 className="mb-0">
            {editedContract?.name}
            {": "}
            <small>Plot rating configuration</small>
          </h4>
        </ModalHeader>
        <ModalBody>
          {editedContract && (
            <PlotRatingConfig contract={editedContract} user={user} />
          )}
        </ModalBody>
      </Modal>

      <LevelHeader
        path={[{ label: "Campaigns" }]}
        metrics={portfolioMetrics(contracts)}
        actions={
          canExport || canRemoveAccess ? (
            <div className="d-flex gap-2">
              {canExport && (
                <Button
                  data-tooltip-id="tooltip"
                  data-tooltip-content="Download your data as an archive"
                  data-tooltip-place="bottom"
                  color="success"
                  size="sm"
                  onClick={() => setIsBulkExportOpen(true)}
                >
                  <i className="fa-solid fa-download me-1" /> Download results
                </Button>
              )}
              {canRemoveAccess && (
                <Button
                  data-tooltip-id="tooltip"
                  data-tooltip-content="Remove your access to all campaigns"
                  data-tooltip-variant="error"
                  data-tooltip-place="bottom"
                  color="danger"
                  outline
                  size="sm"
                  onClick={removeAllAccess}
                >
                  <i className="fa-solid fa-user-xmark me-1" /> Remove all my
                  access
                </Button>
              )}
            </div>
          ) : null
        }
      />

      {/* Mounted only while open so each opening starts from a clean state. */}
      {isBulkExportOpen && (
        <BulkExportDrawer
          isOpen
          onClose={() => setIsBulkExportOpen(false)}
          contracts={contracts}
          trials={user.trials}
          user={user}
        />
      )}

      {contracts.length > 0 && (
        <FilterBar
          query={query}
          onQuery={setQuery}
          placeholder="Search a campaign or company…"
          selects={[
            {
              label: "Company",
              value: company,
              onChange: setCompany,
              allLabel: "All companies",
              options: companyOptions,
            },
          ]}
          extra={
            <Select
              className="cf-rs"
              classNamePrefix="cf-rs"
              isSearchable={false}
              options={SORT_OPTIONS}
              value={SORT_OPTIONS.find((option) => option.value === sort)}
              onChange={(option) => setSort(option.value)}
              aria-label="Sort campaigns"
            />
          }
          trailing={
            <>
              {actionCount > 0 && (
                <label className="btn-action-required">
                  <input
                    type="checkbox"
                    checked={actionRequired}
                    onChange={() => setActionRequired((v) => !v)}
                  />
                  <i className="fa-solid fa-triangle-exclamation" />
                  <span>
                    {actionCount}{" "}
                    {actionCount === 1 ? "site requires" : "sites require"} your
                    attention
                  </span>
                </label>
              )}
              <label
                className={"cf-checkfilter" + (groupBy ? " is-active" : "")}
              >
                <input
                  type="checkbox"
                  checked={groupBy}
                  onChange={() => setGroupBy((v) => !v)}
                />
                <i className="fa-regular fa-object-group" aria-hidden="true" />
                <span>By company</span>
              </label>
              <ViewToggle value={view} onChange={setView} />
            </>
          }
        />
      )}

      {!loading && contracts.length > 0 && tiles.length > 0 && (
        <>
          {groupBy ? (
            <div className="cf-groups">
              {groups.map((group) => (
                <section className="cf-group" key={group.company.id}>
                  <header className="cf-group__head">
                    <h2
                      className="cf-group__name"
                      style={{ color: companyAccent(group.company.name) }}
                    >
                      {group.company.name}
                    </h2>
                    <span className="cf-group__count">
                      {group.campaigns.length}
                    </span>
                  </header>
                  {renderCampaigns(group.campaigns)}
                </section>
              ))}
            </div>
          ) : (
            renderCampaigns(tiles)
          )}
        </>
      )}

      {!loading && contracts.length > 0 && tiles.length === 0 && (
        <EmptyState
          icon="fa-solid fa-magnifying-glass"
          title="No campaign matches your filters"
          text="Try adjusting the search or company."
        />
      )}

      {!loading && contracts.length === 0 && (
        <EmptyState
          icon="fa-regular fa-folder-open"
          title="No campaigns yet"
          text="Your campaigns and sites will appear here."
        />
      )}

      <LoadingImg visible={loading} />
    </div>
  );
};

export default CampaignsPage;
